
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Code, Route, FileCode } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const DebugRoutes = () => {
  const location = useLocation();

  const allRoutes = [
    // Authentication
    { path: '/auth/login', component: 'Auth', status: 'active' },
    { path: '/auth/register', component: 'Auth', status: 'active' },
    { path: '/auth/invite', component: 'Auth', status: 'placeholder' },
    { path: '/auth/reset-password', component: 'Auth', status: 'placeholder' },

    // Patient Portal
    { path: '/', component: 'Dashboard', status: 'active' },
    { path: '/dashboard', component: 'Dashboard', status: 'active' },
    { path: '/appointments', component: 'Appointments', status: 'active' },
    { path: '/appointments/book', component: 'BookAppointment', status: 'active' },
    { path: '/credits', component: 'Credits', status: 'active' },
    { path: '/credits/history', component: 'CreditsHistory', status: 'active' },

    // Staff Portal
    { path: '/staff/dashboard', component: 'StaffDashboard', status: 'active' },
    { path: '/staff/patients', component: 'PatientManagement', status: 'active' },
    { path: '/staff/payments', component: 'PaymentProcessing', status: 'active' },
    { path: '/staff/treatments', component: 'StaffTreatments', status: 'active' },

    // Operations
    { path: '/ops/dashboard', component: 'OperationsDashboard', status: 'active' },
    { path: '/ops/sop', component: 'HQSOPs', status: 'active' },

    // Academy
    { path: '/academy', component: 'Academy', status: 'active' },
    { path: '/academy/dashboard', component: 'AcademyDashboard', status: 'active' },

    // AI Systems
    { path: '/ai/boardroom', component: 'BoardroomDashboard', status: 'placeholder' },

    // Admin
    { path: '/admin', component: 'AdminDashboard', status: 'placeholder' },

    // Debug
    { path: '/debug/routes', component: 'DebugRoutes', status: 'active' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'placeholder': return 'bg-juv-soft-blush text-juv-deep-chestnut';
      case 'missing': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-juv-cream to-juv-sage/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <Code className="h-8 w-8 text-juv-terracotta" />
            <div>
              <h1 className="text-3xl font-bold font-juv-heading text-juv-deep-chestnut">
                Debug: Route Registry
              </h1>
              <p className="mt-1 text-juv-deep-chestnut/70">
                Current route: {location.pathname}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <Route className="h-8 w-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">
                      {allRoutes.filter(r => r.status === 'active').length}
                    </p>
                    <p className="text-sm text-juv-deep-chestnut/70">Active Routes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <FileCode className="h-8 w-8 text-juv-terracotta" />
                  <div>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">
                      {allRoutes.filter(r => r.status === 'placeholder').length}
                    </p>
                    <p className="text-sm text-juv-deep-chestnut/70">Placeholders</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <Code className="h-8 w-8 text-juv-terracotta" />
                  <div>
                    <p className="text-2xl font-bold text-juv-deep-chestnut">
                      {allRoutes.length}
                    </p>
                    <p className="text-sm text-juv-deep-chestnut/70">Total Routes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Route List */}
          <Card className="bg-white shadow-luxury border border-juv-rose-clay/30">
            <CardHeader>
              <CardTitle className="font-juv-heading text-juv-deep-chestnut">
                All Routes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {allRoutes.map((route) => (
                  <div key={route.path} className="flex items-center justify-between p-3 rounded-lg border border-juv-rose-clay/20">
                    <div className="flex items-center space-x-4">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {route.path}
                      </code>
                      <span className="text-sm text-juv-deep-chestnut">
                        → {route.component}
                      </span>
                    </div>
                    <Badge className={getStatusColor(route.status)}>
                      {route.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DebugRoutes;
